import type { Locale } from './enums.js';

/**
 * Closed vocabularies. These are seeded into the `Translation` table and are editable in
 * admin — this file is the seed source, not the runtime lookup. They live here because the
 * storefront, the admin and the seed script all need the same list.
 */

export interface ColorDef {
  /** Raw supplier value, ALL CAPS Polish. Never rendered as-is. */
  raw: string;
  slug: string;
  /** Display-cased Polish. */
  pl: string;
  uk: string;
  /**
   * One hex for a solid swatch, two for the 16 two-tone values (`SZARY + CZARNY`), three
   * for the single multicolour value. Measured from the file — the spec states 12 two-tone,
   * the data has 16. See DECISIONS.md D-007.
   */
  hex: [string] | [string, string] | [string, string, string];
  /** Supplier typo folded onto a canonical slug (`JASNY RÓŹOWY`, `SZARY + BIALY`). */
  aliasOf?: string;
}

/** All 45 distinct values of the Adams `color` column, measured from the file. */
export const COLORS: ColorDef[] = [
  { raw: 'BEŻOWY', slug: 'bezowy', pl: 'Beżowy', uk: 'Бежевий', hex: ['#D8C7AC'] },
  { raw: 'BIAŁY', slug: 'bialy', pl: 'Biały', uk: 'Білий', hex: ['#F4F2EE'] },
  { raw: 'BRĄZ', slug: 'braz', pl: 'Brąz', uk: 'Коричневий', hex: ['#6B4A32'] },
  { raw: 'BRĄZOWY', slug: 'brazowy', pl: 'Brązowy', uk: 'Коричневий', hex: ['#7A563C'] },
  { raw: 'BRĄZ + CAPPUCCINO', slug: 'braz-cappuccino', pl: 'Brąz + cappuccino', uk: 'Коричневий + капучино', hex: ['#6B4A32', '#B79C7F'] },
  { raw: 'BUTELKOWA ZIELEŃ', slug: 'butelkowa-zielen', pl: 'Butelkowa zieleń', uk: 'Пляшково-зелений', hex: ['#2F4A38'] },
  { raw: 'BUTELKOWY ZIELONY', slug: 'butelkowy-zielony', pl: 'Butelkowy zielony', uk: 'Пляшково-зелений', hex: ['#33503C'], aliasOf: 'butelkowa-zielen' },
  { raw: 'CAPPUCCINO', slug: 'cappuccino', pl: 'Cappuccino', uk: 'Капучино', hex: ['#B79C7F'] },
  { raw: 'CAPPUCCINO + BRĄZ', slug: 'cappuccino-braz', pl: 'Cappuccino + brąz', uk: 'Капучино + коричневий', hex: ['#B79C7F', '#6B4A32'] },
  { raw: 'CIEMNY BEŻOWY', slug: 'ciemny-bezowy', pl: 'Ciemny beżowy', uk: 'Темно-бежевий', hex: ['#B9A483'] },
  { raw: 'CIEMNY SZARY', slug: 'ciemny-szary', pl: 'Ciemny szary', uk: 'Темно-сірий', hex: ['#4A4F55'] },
  { raw: 'CIEMNY SZARY + BIAŁY', slug: 'ciemny-szary-bialy', pl: 'Ciemny szary + biały', uk: 'Темно-сірий + білий', hex: ['#4A4F55', '#F4F2EE'] },
  { raw: 'CIEMNY SZARY + CZARNY', slug: 'ciemny-szary-czarny', pl: 'Ciemny szary + czarny', uk: 'Темно-сірий + чорний', hex: ['#4A4F55', '#1E1E20'] },
  { raw: 'CZARNY', slug: 'czarny', pl: 'Czarny', uk: 'Чорний', hex: ['#1E1E20'] },
  { raw: 'CZARNY + ATRAMENTOWY', slug: 'czarny-atramentowy', pl: 'Czarny + atramentowy', uk: 'Чорний + чорнильний', hex: ['#1E1E20', '#2A3552'] },
  { raw: 'CZARNY + BIAŁY', slug: 'czarny-bialy', pl: 'Czarny + biały', uk: 'Чорний + білий', hex: ['#1E1E20', '#F4F2EE'] },
  { raw: 'CZARNY + CZERWONY', slug: 'czarny-czerwony', pl: 'Czarny + czerwony', uk: 'Чорний + червоний', hex: ['#1E1E20', '#A32C2C'] },
  { raw: 'CZARNY + FUKSJA', slug: 'czarny-fuksja', pl: 'Czarny + fuksja', uk: 'Чорний + фуксія', hex: ['#1E1E20', '#B0327A'] },
  { raw: 'CZARNY + LIMONKA', slug: 'czarny-limonka', pl: 'Czarny + limonka', uk: 'Чорний + лаймовий', hex: ['#1E1E20', '#A9C63E'] },
  { raw: 'CZARNY + SZARY', slug: 'czarny-szary', pl: 'Czarny + szary', uk: 'Чорний + сірий', hex: ['#1E1E20', '#8A8F96'] },
  { raw: 'CZARNY + TURKUS', slug: 'czarny-turkus', pl: 'Czarny + turkus', uk: 'Чорний + бірюзовий', hex: ['#1E1E20', '#2E8B94'] },
  { raw: 'GRANATOWY', slug: 'granatowy', pl: 'Granatowy', uk: 'Темно-синій', hex: ['#26344F'] },
  { raw: 'JASNY BRĄZ', slug: 'jasny-braz', pl: 'Jasny brąz', uk: 'Світло-коричневий', hex: ['#A57D5B'] },
  { raw: 'JASNY NIEBIESKI', slug: 'jasny-niebieski', pl: 'Jasny niebieski', uk: 'Світло-синій', hex: ['#8FA9C4'] },
  { raw: 'JASNY RÓŹOWY', slug: 'jasny-rozowy', pl: 'Jasny różowy', uk: 'Світло-рожевий', hex: ['#E3BFC4'] },
  { raw: 'JASNY SZARY', slug: 'jasny-szary', pl: 'Jasny szary', uk: 'Світло-сірий', hex: ['#BFC3C7'] },
  { raw: 'JASNY ZIELONY', slug: 'jasny-zielony', pl: 'Jasny zielony', uk: 'Світло-зелений', hex: ['#9BB08A'] },
  { raw: 'KAKAO', slug: 'kakao', pl: 'Kakao', uk: 'Какао', hex: ['#8B6A55'] },
  { raw: 'KREMOWY', slug: 'kremowy', pl: 'Kremowy', uk: 'Кремовий', hex: ['#EDE3D2'] },
  { raw: 'MIEDZIANY', slug: 'miedziany', pl: 'Miedziany', uk: 'Мідний', hex: ['#B06A3B'] },
  { raw: 'MIODOWY', slug: 'miodowy', pl: 'Miodowy', uk: 'Медовий', hex: ['#D2A253'] },
  { raw: 'NIEBIESKI', slug: 'niebieski', pl: 'Niebieski', uk: 'Синій', hex: ['#3E5C82'] },
  { raw: 'OLIWKOWY', slug: 'oliwkowy', pl: 'Oliwkowy', uk: 'Оливковий', hex: ['#6E7448'] },
  { raw: 'POPIEL', slug: 'popiel', pl: 'Popielaty', uk: 'Попелястий', hex: ['#9A9A97'] },
  { raw: 'POPIEL + BIAŁY', slug: 'popiel-bialy', pl: 'Popielaty + biały', uk: 'Попелястий + білий', hex: ['#9A9A97', '#F4F2EE'] },
  { raw: 'POPIEL + CZARNY', slug: 'popiel-czarny', pl: 'Popielaty + czarny', uk: 'Попелястий + чорний', hex: ['#9A9A97', '#1E1E20'] },
  { raw: 'RÓŻOWY', slug: 'rozowy', pl: 'Różowy', uk: 'Рожевий', hex: ['#D39AA4'] },
  { raw: 'SZARY', slug: 'szary', pl: 'Szary', uk: 'Сірий', hex: ['#8A8F96'] },
  { raw: 'SZARY + BIAŁY', slug: 'szary-bialy', pl: 'Szary + biały', uk: 'Сірий + білий', hex: ['#8A8F96', '#F4F2EE'] },
  { raw: 'SZARY + BIALY', slug: 'szary-bialy-alt', pl: 'Szary + biały', uk: 'Сірий + білий', hex: ['#8A8F96', '#F4F2EE'], aliasOf: 'szary-bialy' },
  { raw: 'SZARY + CZARNY', slug: 'szary-czarny', pl: 'Szary + czarny', uk: 'Сірий + чорний', hex: ['#8A8F96', '#1E1E20'] },
  { raw: 'TURKUSOWY', slug: 'turkusowy', pl: 'Turkusowy', uk: 'Бірюзовий', hex: ['#2E8B94'] },
  { raw: 'WIELOKOLOROWY', slug: 'wielokolorowy', pl: 'Wielokolorowy', uk: 'Різнокольоровий', hex: ['#C0705A', '#4E7A8C', '#D9B23C'] },
  { raw: 'ZIELONY', slug: 'zielony', pl: 'Zielony', uk: 'Зелений', hex: ['#4E6B4A'] },
  { raw: 'ŻÓŁTY', slug: 'zolty', pl: 'Żółty', uk: 'Жовтий', hex: ['#D9B23C'] },
];

/** 16 values are two-tone and need a split swatch; 1 is multicolour and needs three stops. */
/**
 * A second, smaller colour vocabulary: the `Kolor łóżka` axis names its shades in prose
 * plus an upholstery code — `odcień szarości H96` — rather than using the 45-value
 * `color` column. Ten distinct values, measured from the feed, so they are seeded like any
 * other closed vocabulary rather than left to render as an unmapped chip.
 */
export const BED_SHADES: ColorDef[] = [
  { raw: 'odcień beżu H1', slug: 'odcien-bezu-h1', pl: 'Odcień beżu H1', uk: 'Відтінок бежевого H1', hex: ['#D6C6AA'] },
  { raw: 'odcień beżu T2', slug: 'odcien-bezu-t2', pl: 'Odcień beżu T2', uk: 'Відтінок бежевого T2', hex: ['#C9B392'] },
  { raw: 'odcień brązu T6', slug: 'odcien-brazu-t6', pl: 'Odcień brązu T6', uk: 'Відтінок коричневого T6', hex: ['#7A563C'] },
  { raw: 'odcień ciemnej szarości T15', slug: 'odcien-ciemnej-szarosci-t15', pl: 'Odcień ciemnej szarości T15', uk: 'Відтінок темно-сірого T15', hex: ['#4A4F55'] },
  { raw: 'odcień czerni H100', slug: 'odcien-czerni-h100', pl: 'Odcień czerni H100', uk: 'Відтінок чорного H100', hex: ['#22262B'] },
  { raw: 'odcień niebieskiego T13', slug: 'odcien-niebieskiego-t13', pl: 'Odcień niebieskiego T13', uk: 'Відтінок синього T13', hex: ['#4A6585'] },
  { raw: 'odcień niebieskiego T31', slug: 'odcien-niebieskiego-t31', pl: 'Odcień niebieskiego T31', uk: 'Відтінок синього T31', hex: ['#2E4667'] },
  { raw: 'odcień szarości H10', slug: 'odcien-szarosci-h10', pl: 'Odcień szarości H10', uk: 'Відтінок сірого H10', hex: ['#9AA0A6'] },
  { raw: 'odcień szarości H96', slug: 'odcien-szarosci-h96', pl: 'Odcień szarości H96', uk: 'Відтінок сірого H96', hex: ['#7C838B'] },
  { raw: 'odcień szarości T14', slug: 'odcien-szarosci-t14', pl: 'Odcień szarości T14', uk: 'Відтінок сірого T14', hex: ['#8A8F96'] },
];

/** Every colour the catalogue can show a swatch for, from either vocabulary. */
export const ALL_COLORS: ColorDef[] = [...COLORS, ...BED_SHADES];

export const TWO_TONE_COUNT = COLORS.filter((c) => c.hex.length === 2).length;
export const MULTICOLOUR_COUNT = COLORS.filter((c) => c.hex.length === 3).length;

/** The 13 variant axes measured in the Adams title column, with their display labels. */
export interface AxisDef {
  raw: string;
  slug: string;
  pl: string;
  uk: string;
  /** How the storefront renders it. `side` gets mirrored diagrams, not a dropdown. */
  widget: 'swatch' | 'side' | 'size' | 'chip' | 'toggle';
  /** Axes that are the same concept under two supplier names fold onto one slug. */
  foldsInto?: string;
}

export const AXES: AxisDef[] = [
  { raw: 'Kolor', slug: 'kolor', pl: 'Kolor', uk: 'Колір', widget: 'swatch' },
  { raw: 'Kolor łóżka', slug: 'kolor-lozka', pl: 'Kolor łóżka', uk: 'Колір ліжка', widget: 'swatch' },
  { raw: 'Materiał łózka', slug: 'material-lozka', pl: 'Materiał łóżka', uk: 'Матеріал ліжка', widget: 'chip' },
  { raw: 'Rozmiar łózka', slug: 'rozmiar', pl: 'Rozmiar łóżka', uk: 'Розмір ліжка', widget: 'size' },
  { raw: 'Wymiar', slug: 'rozmiar', pl: 'Rozmiar', uk: 'Розмір', widget: 'size', foldsInto: 'rozmiar' },
  { raw: 'Pojemnik na pościel', slug: 'pojemnik', pl: 'Pojemnik na pościel', uk: 'Ящик для білизни', widget: 'chip' },
  { raw: 'Wypełnienie', slug: 'wypelnienie', pl: 'Wypełnienie', uk: 'Наповнення', widget: 'chip' },
  { raw: 'Strona', slug: 'strona', pl: 'Strona', uk: 'Сторона', widget: 'side' },
  { raw: 'Rodzaj tkaniny', slug: 'grupa-tkaniny', pl: 'Grupa tkaniny', uk: 'Група тканини', widget: 'chip' },
  { raw: 'Rodzaj podłokietnika', slug: 'podlokietnik', pl: 'Rodzaj podłokietnika', uk: 'Тип підлокітника', widget: 'chip' },
  { raw: 'Dodatki', slug: 'dodatki', pl: 'Dodatki', uk: 'Додатки', widget: 'chip' },
  { raw: 'Zagłowie', slug: 'zaglowie', pl: 'Zagłówek', uk: 'Узголів\'я', widget: 'toggle' },
  { raw: 'Funkcja spania', slug: 'funkcja-spania', pl: 'Funkcja spania', uk: 'Спальна функція', widget: 'toggle' },
];

/** Axis values that are a closed vocabulary get seeded translations too. */
export const AXIS_VALUES: Record<string, Array<{ raw: string; pl: string; uk: string }>> = {
  strona: [
    { raw: 'LEWA', pl: 'Lewa', uk: 'Ліва' },
    { raw: 'PRAWA', pl: 'Prawa', uk: 'Права' },
  ],
  pojemnik: [
    { raw: 'Tak', pl: 'Tak', uk: 'Так' },
    { raw: 'Nie', pl: 'Nie', uk: 'Ні' },
    { raw: '2 pojemniki', pl: '2 pojemniki', uk: '2 ящики' },
  ],
  wypelnienie: [
    { raw: 'Piankowe', pl: 'Piankowe', uk: 'Пінне' },
    { raw: 'Sprężynowe', pl: 'Sprężynowe', uk: 'Пружинне' },
    { raw: 'Piankowo-sprężynowe', pl: 'Piankowo-sprężynowe', uk: 'Пінно-пружинне' },
  ],
  zaglowie: [
    { raw: 'Tak', pl: 'Tak', uk: 'Так' },
    { raw: 'Nie', pl: 'Nie', uk: 'Ні' },
  ],
  'funkcja-spania': [
    { raw: 'Tak', pl: 'Tak', uk: 'Так' },
    { raw: 'Nie', pl: 'Nie', uk: 'Ні' },
  ],
  'grupa-tkaniny': [
    { raw: 'I grupa', pl: 'I grupa', uk: 'I група' },
    { raw: 'II grupa', pl: 'II grupa', uk: 'II група' },
    { raw: 'III Grupa', pl: 'III grupa', uk: 'III група' },
  ],
  'material-lozka': [
    { raw: 'velvet', pl: 'Welur', uk: 'Велюр' },
    { raw: 'plecionka', pl: 'Plecionka', uk: 'Плетінка' },
    { raw: 'tkanina', pl: 'Tkanina', uk: 'Тканина' },
    { raw: 'ekoskóra', pl: 'Ekoskóra', uk: 'Екошкіра' },
  ],
  podlokietnik: [
    { raw: 'Prosty', pl: 'Prosty', uk: 'Прямий' },
    { raw: 'Zaokrąglony', pl: 'Zaokrąglony', uk: 'Заокруглений' },
  ],
};

/** The folded category tree. Supplier raw values map here through `CategoryAlias`. */
export interface CategoryDef {
  slug: string;
  pl: string;
  uk: string;
  parent?: string;
  position: number;
}

export const CATEGORIES: CategoryDef[] = [
  // Seating — Adams' whole range plus Artisan's benches and chairs.
  { slug: 'siedziska', pl: 'Meble wypoczynkowe', uk: 'М\'які меблі', position: 1 },
  { slug: 'narozniki', pl: 'Narożniki', uk: 'Кутові дивани', parent: 'siedziska', position: 1 },
  { slug: 'kanapy', pl: 'Kanapy', uk: 'Дивани', parent: 'siedziska', position: 2 },
  { slug: 'sofy', pl: 'Sofy', uk: 'Софи', parent: 'siedziska', position: 3 },
  { slug: 'fotele', pl: 'Fotele', uk: 'Крісла', parent: 'siedziska', position: 4 },
  { slug: 'pufy', pl: 'Pufy', uk: 'Пуфи', parent: 'siedziska', position: 5 },
  { slug: 'podnozki', pl: 'Podnóżki', uk: 'Підставки для ніг', parent: 'siedziska', position: 6 },
  { slug: 'system-modulowy', pl: 'Systemy modułowe', uk: 'Модульні системи', parent: 'siedziska', position: 7 },
  { slug: 'lawki', pl: 'Ławki', uk: 'Лавки', parent: 'siedziska', position: 8 },
  { slug: 'krzesla', pl: 'Krzesła', uk: 'Стільці', parent: 'siedziska', position: 9 },

  { slug: 'sypialnia', pl: 'Sypialnia', uk: 'Спальня', position: 2 },
  { slug: 'lozka', pl: 'Łóżka', uk: 'Ліжка', parent: 'sypialnia', position: 1 },
  { slug: 'materace', pl: 'Materace', uk: 'Матраци', parent: 'sypialnia', position: 2 },
  // 150 products, the single largest group in the catalogue. It was inside `Szafki i
  // komody`, where a bedside table is neither.
  { slug: 'szafki-nocne', pl: 'Szafki nocne', uk: 'Приліжкові тумби', parent: 'sypialnia', position: 3 },

  // Storage. Previously one `Szafki i komody` bucket that absorbed bedsides, media units,
  // sideboards and cabinets alike.
  { slug: 'przechowywanie', pl: 'Przechowywanie', uk: 'Зберігання', position: 3 },
  { slug: 'komody', pl: 'Komody', uk: 'Комоди', parent: 'przechowywanie', position: 1 },
  { slug: 'szafki-rtv', pl: 'Szafki RTV', uk: 'ТВ-тумби', parent: 'przechowywanie', position: 2 },
  { slug: 'kredensy', pl: 'Kredensy', uk: 'Серванти', parent: 'przechowywanie', position: 3 },
  { slug: 'regaly', pl: 'Regały', uk: 'Стелажі', parent: 'przechowywanie', position: 4 },
  { slug: 'szafki', pl: 'Szafki', uk: 'Шафки', parent: 'przechowywanie', position: 5 },

  // Tables. `Biurka i stoliki` held coffee tables, consoles, side tables and desks at once.
  { slug: 'stoly', pl: 'Stoły i biurka', uk: 'Столи та письмові столи', position: 4 },
  { slug: 'stoliki-kawowe', pl: 'Stoliki kawowe', uk: 'Журнальні столики', parent: 'stoly', position: 1 },
  { slug: 'konsole', pl: 'Konsole', uk: 'Консолі', parent: 'stoly', position: 2 },
  { slug: 'stoliki-boczne', pl: 'Stoliki boczne', uk: 'Приставні столики', parent: 'stoly', position: 3 },
  { slug: 'biurka', pl: 'Biurka', uk: 'Письмові столи', parent: 'stoly', position: 4 },

  { slug: 'dodatki', pl: 'Dodatki', uk: 'Аксесуари', position: 5 },
  { slug: 'poduszki', pl: 'Poduszki', uk: 'Подушки', parent: 'dodatki', position: 1 },
  { slug: 'lustra', pl: 'Lustra', uk: 'Дзеркала', parent: 'dodatki', position: 2 },
];

export const SHIPPING_PROFILE_LABELS: Record<string, { pl: string; uk: string }> = {
  parcel: { pl: 'Kurier', uk: 'Кур\'єр' },
  one_man: { pl: 'Dostawa z wniesieniem', uk: 'Доставка із занесенням' },
  palett: { pl: 'Transport paletowy', uk: 'Палетне перевезення' },
  adams_parcels: { pl: 'Kurier — przesyłka wielopaczkowa', uk: 'Кур\'єр — багатопакункове відправлення' },
  manual_parcel: { pl: 'Kurier — pozycje własne', uk: 'Кур\'єр — власні позиції' },
};

export const ORDER_STATUS_LABELS: Record<string, { pl: string; uk: string }> = {
  new: { pl: 'Nowe', uk: 'Нове' },
  paid: { pl: 'Opłacone', uk: 'Оплачено' },
  sent_to_supplier: { pl: 'Wysłane do producenta', uk: 'Передано виробнику' },
  shipped: { pl: 'Wysłane', uk: 'Відправлено' },
  delivered: { pl: 'Dostarczone', uk: 'Доставлено' },
  cancelled: { pl: 'Anulowane', uk: 'Скасовано' },
  refunded: { pl: 'Zwrócone', uk: 'Повернуто' },
};

export function localeValue<T extends { pl: string; uk: string }>(def: T, locale: Locale): string {
  return locale === 'uk' ? def.uk : def.pl;
}
